import { useNavigate } from "react-router-dom";
import config from "../config";
import React, { useState, useEffect } from "react";
import Button from "./common/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "./common/Card";
import { EyeIcon } from "./icons";
import { useForm } from "react-hook-form";
import * as utils from "../utils/utils";
import { useAuth } from "../lib/AuthContext";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";
import LanguageSwitcher from "./common/LanguageSwitcher";
import AuthPageLoader from "./common/AuthPageLoader";

/** Solo visible con ?dev=true — acceso rápido QA */
const QUICK_LOGIN_PRESETS = [
  {
    label: "Administrador",
    email: "admin@aristas.com",
    password: "changeme",
  },
  {
    label: "Paciente",
    email: "info.promedar+paciente1@gmail.com",
    password: "changeme",
  },
  {
    label: "Profesional",
    email: "info.promedar+medico1234@gmail.com",
    password: "123456",
  },
  {
    label: "Secretaria",
    email: "info.promedar+secreprueba@gmail.com",
    password: "changeme",
  },
  {
    label: "Institución",
    email: "info.promedar+institucion3@gmail.com",
    password: "123456",
  },
  {
    label: "Asociación",
    email: "info.promedar+aso1@gmail.com",
    password: "123456",
  },
];

export default function Login() {
  const { t } = useBrandTranslation();
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const urlParams = new URLSearchParams(window.location.search);
  const devMode = urlParams.get('dev');
  const showLoginButtons = (devMode === 'true') ? true : false;

  useEffect(() => {
    if (!loading && user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const result = await login(email, password);
    if (!result?.success) {
      setError(
        result?.error ||
          t(
            "auth.invalidCredentials",
            "Credenciales inválidas. Por favor, verifica tu email y contraseña."
          )
      );
    } else {
      navigate("/");
    }

    setIsLoading(false);
  };

  async function quickLoginPreset(presetEmail, presetPassword) {
    setEmail(presetEmail);
    setPassword(presetPassword);
    setIsPasswordVisible(true);
    setIsLoading(true);
    setError("");

    const result = await login(presetEmail, presetPassword);
    setIsLoading(false);

    if (!result?.success) {
      setError(
        result?.error ||
          t(
            "auth.invalidCredentials",
            "Credenciales inválidas. Por favor, verifica tu email y contraseña."
          )
      );
      return;
    }

    navigate("/");
  }

  if (loading) {
    return <AuthPageLoader />;
  }

  if (isLoading) {
    return <AuthPageLoader message={t("auth.signingIn", "Iniciando sesión...")} />;
  }

  return (
    <div className="flex flex-col w-full min-h-screen bg-cold-white">
      <div className="flex h-[calc(100vh-4rem)]">
        <main className="flex-1">
          <div className="h-full overflow-auto mt-4 px-4">
            <div className="flex flex-col items-center justify-center h-full mb-4">
              <div className="mb-3">
                <LanguageSwitcher variant="dark" />
              </div>
              <div className="flex items-center justify-center mb-4">
                <img
                  src={utils.getBrandLogoSrc()}
                  alt="logo"
                  className={utils.getBrandLogoClassName()}
                />
                {!utils.isBinapsisBrand() && (
                  <h1 className="inline-block text-2xl sm:text-3xl text-gray-400 pl-2 tracking-tight ">
                    {config.brand}
                  </h1>
                )}
              </div>
              {false ? (
                <div className="flex flex-col items-start justify-center p-4 rounded-lg w-[100%] max-w-[500px] shadow-lg border border-gray-100 bg-white">
                  <LoginFakeForm />
                </div>
              ) : (
                <div className="flex flex-col items-start justify-center p-4 sm:p-6 rounded-lg w-full max-w-[500px] shadow-lg border border-gray-100 bg-white mx-2">
                  <header className="mb-4">
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">
                      {t("auth.signIn", "Sign in")}
                    </h1>
                  </header>

                {showLoginButtons && (
                  <div className="w-full mb-4 space-y-2">
                    <p className="text-xs font-medium text-gray-600">
                      {t("auth.quickAccess", "Acceso rápido (cuentas de prueba)")}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full">
                      {QUICK_LOGIN_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          disabled={isLoading}
                          className="flex items-center justify-center text-gray-600 hover:text-gray-900 border rounded p-2 text-center hover:bg-gray-50 text-xs sm:text-sm disabled:opacity-50"
                          onClick={() =>
                            quickLoginPreset(preset.email, preset.password)
                          }
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                  <form onSubmit={onSubmit} className="w-full flex flex-col">
                    <div className="text-xs-special mb-2 font-sans text-gray-900 block">
                      {t("auth.email", "Email")}
                    </div>
                    <input
                      type="text"
                      {...register("username", { required: true })}
                      className="mt-2 rounded border border-slate-200 p-4 pl-8 text-slate-500 w-full"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {errors.username && (
                      <span className="px-2 text-red-500">* {t("common.required", "Obligatorio")}</span>
                    )}
                    <div className="flex flex-col mt-6">
                      <div className="text-xs-special mb-2 font-sans text-gray-900 block">
                        {t("auth.password", "Password")}
                      </div>
                      <div className="flex">
                        <input
                          type={isPasswordVisible ? "text" : "password"}
                          {...register("password", { required: true })}
                          className="mt-2 w-full rounded border border-slate-200 p-4 pl-8 text-slate-500"
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <div
                          className="relative mt-8 -ml-5 right-2.5	text-gray-900 cursor-pointer"
                          onClick={() =>
                            setIsPasswordVisible(!isPasswordVisible)
                          }
                        >
                          <EyeIcon className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                    {errors.password && (
                      <span className="px-2 text-red-500">* {t("common.required", "Obligatorio")}</span>
                    )}
                    <Button className="mt-6">{t("auth.signIn", "Sign in")}</Button>
                    {error && <span className="p-2 text-red-500">{error}</span>}
                  </form>
                </div>
              )}

              <div className="flex flex-col items-center justify-center mt-4">
                <p className="mt-4 text-sm text-gray-500">
                  {t("auth.noAccount", "¿No tenés cuenta?")}{" "}
                  <a href="/register" className="text-gray-900 underline">
                    {t("auth.registerHere", "Registrate acá")}
                  </a>
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {t("auth.forgotPassword", "Olvidaste tu contraseña?")}{" "}
                  <a
                    href="/forgot-password"
                    className="text-gray-900 underline"
                  >
                    {t("auth.recoverPassword", "Recuperar contraseña")}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function LoginFakeForm() {
  const { login, switchUser } = useAuth();
  const navigate = useNavigate();

  const handleQuickLogin = async (userId) => {
    try {
      await switchUser(userId);
      navigate("/home");
    } catch (error) {
      console.error("Error al cambiar usuario:", error);
      customToast(t("auth.loginError", "Error al iniciar sesión. Intenta nuevamente."), "danger");
    }
  };

  function getUserUUid(userId) {
    const userUuids = {
      "doctor-1": "550e8400-e29b-41d4-a716-446655440001",
      "secretary-1": "550e8400-e29b-41d4-a716-446655440002",
      "patient-1": "550e8400-e29b-41d4-a716-446655440003",
    };
    return userUuids[userId] || "550e8400-e29b-41d4-a716-446655440000";
  }

  return (
    <div className="flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("auth.demoTitle", "Demo - Acceso Rápido")}</CardTitle>
            <CardDescription>
              Haz clic para probar diferentes roles
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              className="w-full bg-gray-100"
              onClick={() => handleQuickLogin(getUserUUid("doctor-1"))}
            >
              Ingresar como Médico
            </Button>
            <Button
              variant="outline"
              className="w-full bg-gray-100"
              onClick={() => handleQuickLogin(getUserUUid("secretary-1"))}
            >
              Ingresar como Secretaria
            </Button>
            <Button
              variant="outline"
              className="w-full bg-gray-100"
              onClick={() => handleQuickLogin(getUserUUid("patient-1"))}
            >
              Ingresar como Paciente
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
